import React from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { useRouter } from 'next/router'

import {
  getXCLEARCorrelationInfo,
  setAccessToken,
  setSessionId,
} from '@api/utils'
import { useConfigContext } from '@contexts/ConfigContext'
import { usePricingConfigContext } from '@contexts/PricingContext'
import { useFocusContext } from '@contexts/FocusTaskContext'

import { useTokenVerification } from './useTokenVerification'
import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'
import { Enrollment } from '@clear-denver/tpc-openapi-typescript'

const FIFTEEN_MINUTES = 1_000 * 60 * 15

export const getInactivityTimeout = (INACTIVITY_TIMEOUT = FIFTEEN_MINUTES) =>
  INACTIVITY_TIMEOUT

export function useSessionTimer(
  onTimeout?: () => void,
  disableTokenVerification?: boolean,
  inactivityTimeout?: number
) {
  const router = useRouter()
  const { refetchConfig } = useConfigContext()
  const { fetchPricing } = usePricingConfigContext()
  const { setFocusTask } = useFocusContext()

  useTokenVerification(disableTokenVerification)

  const sessionTimeoutHandler = async () => {
    return new Promise<boolean>((resolve) => {
      const pipe = createPipe({
        xCLEARCorrelationInfo: getXCLEARCorrelationInfo(),
        type: 'ABANDONED',
        reason: 'TIMEOUT',
        route: router.route,
      })

      pipe.start()

      setFocusTask({
        isFocusing: true,
        header: 'Your session has expired',
        content: (
          <>For your security, you’ll need to enter your information again.</>
        ),
        cta: 'Continue',
        onConfirm: async function refresh() {
          pipe.ack()

          try {
            onTimeout?.()
          } catch (err: unknown) {
            pipe.error(err)
          }

          await Promise.allSettled([refetchConfig(), fetchPricing()])

          setAccessToken('')
          setSessionId('')

          const route = '/'
          pipe.conclude(route)
          resolve(router.push(route))
        },
        canDismiss: false /** users must select CTA */,
      })
    })
  }

  const idleTimerFunctions = useIdleTimer({
    timeout: getInactivityTimeout(inactivityTimeout),
    onIdle: sessionTimeoutHandler,
    debounce: 500,
  })

  return {
    ...idleTimerFunctions,
    sessionTimeoutHandler,
  }
}

const PREFIX = '[TIMEOUT] '

type Meta = Record<string, string> & { xCLEARCorrelationInfo: string }

/** telemetry wrapper for the timeout flow */
export function createPipe(meta: Meta) {
  function onStart(meta: Meta) {
    dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
      payload: {
        message: PREFIX + 'Unable to continue',
      },
      metadata: {
        ...meta,
        kpi: KPI.USER_INTERACTION,
        event: DiscreteEvent.TIMEOUT_VIEW,
      },
    })
  }

  function onError(error: unknown, meta: Meta) {
    const err = (typeof error === 'object' ? error : {}) as Record<
      string,
      string
    >
    dispatchCustomEvent(Enrollment.LogEntryLevelEnum.ERROR, {
      payload: {
        message:
          PREFIX + (err?.message ?? 'error thrown when invoking onTimeout()'),
        stack: err?.stack ?? 'not available',
      },
      metadata: {
        ...meta,
        kpi: KPI.USER_INTERACTION,
        event: DiscreteEvent.CLIENT_SIDE_ERROR,
      },
    })
  }

  function onAcknowledged(meta: Meta) {
    dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
      payload: {
        message: PREFIX + 'User Acknowledged',
      },
      metadata: {
        ...meta,
        kpi: KPI.USER_INTERACTION,
        event: DiscreteEvent.TIMEOUT_ACKNOWLEDGED,
      },
    })
  }

  function onConclude(meta: Meta) {
    dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
      payload: {
        message: PREFIX + 'User sent to landing page',
      },
      metadata: {
        ...meta,
        kpi: KPI.USER_INTERACTION,
        event: DiscreteEvent.TIMEOUT_FLOW_CONCLUDED,
      },
    })
  }

  return {
    start: () => onStart(meta),
    error: (error: unknown) => onError(error, meta),
    ack: () => onAcknowledged(meta),
    conclude: (route: string) => onConclude({ ...meta, route }),
  }
}
